import { formatAssetAmount } from '@exodus/formatting/lib/asset';
import ActivityIcon from '@passkeys/ui/activity-icon';
import Skeleton from '@passkeys/ui/skeleton';
import Text from '@passkeys/ui/text';
import { useMemo } from 'react';

import type { ActivityItem } from '@/ui/flux/tx-log/types';
import { getIsFailed } from '@/ui/flux/tx-log/utils.js';
import useEnabledAssets from '@/ui/hooks/use-enabled-assets.js';
import ActivityStatus from '../../../components/activity-status/activity-status.js';

type HeroProps = {
  activityItem?: ActivityItem;
};

function Hero({ activityItem }: HeroProps) {
  const assetName = activityItem?.assetName;
  const order = activityItem?.order;
  const isSwap = !!order;

  const assets = useEnabledAssets();

  const toAsset = useMemo(() => {
    if (isSwap) {
      return assets[order.toAsset];
    }

    return assetName ? assets[assetName] : undefined;
  }, [assetName, assets, isSwap, order]);
  const fromAsset = isSwap ? assets[order.fromAsset] : undefined;

  const amount = isSwap ? order.toAmount : activityItem?.displayAmount;
  const assetNames = useMemo(() => {
    if (isSwap && toAsset && fromAsset) {
      return [toAsset.name, fromAsset.name];
    }

    return assetName ? [assetName] : [];
  }, [assetName, fromAsset, isSwap, toAsset]);
  const assetDisplayNames = useMemo(() => {
    if (isSwap && toAsset && fromAsset) {
      return [toAsset.displayName, fromAsset.displayName];
    }

    return assetName ? [assetName] : [];
  }, [assetName, fromAsset, isSwap, toAsset]);

  const isFailed = getIsFailed(activityItem);

  const loading = !activityItem;

  return (
    <div className="mb-6 flex flex-col items-center gap-6">
      {loading ? (
        <Skeleton width={80} height={80} />
      ) : (
        <ActivityIcon
          assetNames={assetNames}
          displayNames={assetDisplayNames}
          typeIcon={activityItem.icon}
          size="large"
        />
      )}

      <div className="flex flex-col items-center">
        {loading ? (
          <>
            <Skeleton width={160} height={32} className="my-[3px]" />
            <Skeleton
              width={80}
              height={24}
              className="mt-1"
              borderRadius={9999}
            />
          </>
        ) : (
          <>
            {amount && (
              <Text size={32} dataTestId="hero-asset-amount">
                {(activityItem.received || isSwap) && '+'}
                {formatAssetAmount(amount.toDefaultNumber(), {
                  useGrouping: true,
                  maxSignificant: 6,
                })}{' '}
                {toAsset?.displayTicker}
              </Text>
            )}

            <ActivityStatus
              className="mt-1"
              dataTestId="hero-transaction-status"
              value={
                activityItem.pending
                  ? 'pending'
                  : isFailed
                    ? 'failed'
                    : 'completed'
              }
            />
          </>
        )}
      </div>
    </div>
  );
}

export default Hero;
